/**
 * Action toolbar for the CPT codes table: "Report Inaccuracy" and
 * "Save Changes" buttons, plus the CIG-note confirmation dialog.
 *
 * @module CptCodesToolbar
 */
import { ButtonWithTooltip } from "@risalabs_frontend_org/oasis-ui-kit";
import { Button } from "risa-oasis-ui_v2";
import UpdateCigNoteConfirmationDialog from "../../../../../components/modals/updateCigNoteConfirmationDialog/updateCigNoteConfirmationDialog";
import ValidationMessageList from "../../../../../components/validationMessageList/validationMessageList";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { ModalId } from "../../../../../enums/modalId";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { PlusCircle } from "../../../../../svg/plusCircle";
import { useCptLinkedOrdersConfirmationView } from "../hooks/useCptLinkedOrdersConfirmationView";
import { useSaveChangesValidationMessages } from "../hooks/useSaveChangesValidationMessages";

/**
 * Props for {@link CptCodesToolbar}.
 */
interface CptCodesToolbarProps {
  hasChanges: boolean;
  isSaving: boolean;
  hasCptTableAnyError: boolean;
  hasAuthLetterFieldMismatch?: boolean;
  openedModalId: string | null;
  showAddProcedure?: boolean;
  confirmationSource?: "j_codes" | "cig_notes" | "documents" | "cpt_codes";
  /** Live CPT rows; used to detect mapped statuses for linked-order filtering. */
  tableData: CptCodesTableRowModel[];
  /** Parent order DOS; scopes linked siblings to later dates when mapped. */
  parentDateOfService?: string;
  onReportInaccuracy: () => void;
  onSaveLog: () => void;
  onSaveChanges: (selectedOrderIds?: string[]) => Promise<void>;
  dispatch: AppDispatch;
}

/**
 * Renders the "Report Inaccuracy" and "Save Changes" buttons along with
 * the CIG-note confirmation dialog that gates the save flow.
 *
 * @param props - Toolbar actions, table state, and modal wiring
 * @returns Save/report controls and optional linked-orders confirmation modal
 */
const CptCodesToolbar = ({
  hasChanges,
  isSaving,
  hasCptTableAnyError,
  hasAuthLetterFieldMismatch = false,
  openedModalId,
  onReportInaccuracy,
  onSaveLog,
  onSaveChanges,
  dispatch,
  showAddProcedure = false,
  confirmationSource = "j_codes",
  tableData,
  parentDateOfService,
}: CptCodesToolbarProps) => {
  const linkedOrdersView = useCptLinkedOrdersConfirmationView(
    tableData,
    parentDateOfService,
  );

  const { validationMessages, isSaveDisabled } =
    useSaveChangesValidationMessages({
      hasChanges,
      isSaving,
      hasCptTableAnyError,
      hasAuthLetterFieldMismatch,
    });

  return (
    <>
      <div className="mt-4 flex flex-row justify-end gap-2">
        {showAddProcedure && (
          <>
            <div
              className="mr-auto flex cursor-pointer self-center text-tiny font-bold text-tertiaryBlue-4 hover:text-tertiaryBlue-5"
              onClick={() => {
                dispatch(
                  setOpenedModalId({ id: ModalId.ADD_NEW_CPT_CODE_MODAL }),
                );
              }}
            >
              <PlusCircle width={16} height={16} />
              <span className="ml-1">Add Procedure</span>
            </div>
          </>
        )}
        <Button
          buttonType="danger"
          size="medium"
          disabled={false}
          onClick={() => {
            onReportInaccuracy();
            dispatch(setOpenedModalId({ id: ModalId.AUTH_INACCURACY_MODAL }));
          }}
        >
          Report Inaccuracy
        </Button>
        <ButtonWithTooltip
          showTooltip={isSaveDisabled}
          tooltipContent={
            <ValidationMessageList messages={validationMessages} />
          }
          disabled={isSaveDisabled}
          children={isSaving ? "Saving..." : "Save Changes"}
          buttonType="primary"
          size="medium"
          onClick={() => {
            onSaveLog();
            dispatch(
              setOpenedModalId({
                id: ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG,
              }),
            );
          }}
        />
      </div>
      {openedModalId === ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG && (
        <UpdateCigNoteConfirmationDialog
          onSave={onSaveChanges}
          onClose={() => dispatch(closeModal())}
          onCancel={() => dispatch(closeModal())}
          loading={isSaving}
          source={confirmationSource}
          linkedOrdersView={{
            expandableTableData: linkedOrdersView.expandableTableData,
            siblingOrdersError: linkedOrdersView.siblingOrdersError,
            selectedSiblingOrderIds: linkedOrdersView.selectedSiblingOrderIds,
          }}
        />
      )}
    </>
  );
};

export default CptCodesToolbar;
